import type { AccountChat } from "./accountChatsParser"

/**
 * The `silent-fuel-alerts` flag silences Slick's automations in driver rooms.
 * Despite the flag name, it is not just fuel alerts: silenced is every SlickAI
 * message the driver ALSO got. An `internal` one is dispatcher-only, so it is
 * Slick talking to the dispatcher and it stays — see
 * `ChatHelpers.isSilenceableSlickAIMessage` for that half.
 *
 * The axis is the SENDER, not the room: a driver's message in the same room is
 * never silenced. The room only decides WHERE the sender rule applies, so a
 * SlickAI answer the dispatcher asked for elsewhere still shows up.
 *
 * The PostHog name lies, so the constant carries the truth and the string stays
 * in one place.
 */
export const SILENT_SLICK_AI_FLAG = "silent-fuel-alerts"

/** The app-wide definition of a driver room. Kept in one place because the
 * list drifted three times: two call sites carried only the first three. */
/** Per-vehicle rooms bridged to the truck's on-board unit by driver_bot
 * (Transics TX-TANGO or Platform Science FleetWorks) — gps publishes their
 * location/route/tacho under the room JID and they have no driver_a/b. */
export const OBU_ROOM_GROUPS = ["transics_driver", "platform_science_driver"]

export const isObuRoomGroup = (group?: string): boolean =>
  !!group && OBU_ROOM_GROUPS.includes(group)

const DRIVER_ROOM_GROUPS = [
  "driver",
  "shared_driver",
  "double_crew",
  "driver_muc",
  ...OBU_ROOM_GROUPS,
]

export const isDriverRoomGroup = (group?: string): boolean =>
  !!group && DRIVER_ROOM_GROUPS.includes(group)

/**
 * Builds the per-room switch. `flagEnabled` comes from the caller, because the
 * flag is read reactively in components (`useFeatureFlagEnabled`) and through
 * `posthog.isFeatureEnabled` in selectors and thunks. Both read
 * `SILENT_SLICK_AI_FLAG`; never cache the value, PostHog resolves it async —
 * the count selectors turn reselect's argument cache off for that reason.
 */
export const buildIsSlickAISilenced =
  (flagEnabled: boolean, accountChats: Record<string, AccountChat>) =>
  (chatJid: string): boolean =>
    flagEnabled && isDriverRoomGroup(accountChats[chatJid]?.group)
